import { useEffect, useState } from 'preact/hooks';
import { postGoogleIdToken, requestGoogleIdToken } from '../../lib/google-auth.js';

function PasswordField({ id, name, placeholder, error, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div class="pp-auth-field">
      <span class="pp-auth-field__icon" aria-hidden="true">
        <i class="fas fa-lock" />
      </span>
      <input
        type={visible ? 'text' : 'password'}
        id={id}
        name={name}
        class={`pp-auth-input${error ? ' pp-auth-input--error' : ''}`}
        placeholder={placeholder}
        required
        autoComplete={autoComplete}
      />
      <button
        type="button"
        class="pp-auth-field__toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Şifreyi gizle' : 'Şifreyi göster'}
      >
        <i class={`fas ${visible ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
      </button>
    </div>
  );
}

export function AuthLoginPanel({ config }) {
  const [tab, setTab] = useState('email');
  const [googleError, setGoogleError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const loginAction = config.loginUrl + (config.nextUrl ? `?next=${encodeURIComponent(config.nextUrl)}` : '');

  const googleEnabled = Boolean(config.googleAuthEnabled && config.googleClientId);

  async function handleGoogleLogin() {
    if (!googleEnabled || !config.loginGoogleUrl) return;
    setGoogleError('');
    setGoogleLoading(true);
    try {
      const token = await requestGoogleIdToken(config.googleClientId);
      const data = await postGoogleIdToken(config.loginGoogleUrl, token, config.csrfToken);
      const next = data.redirect_url || '/';
      window.location.href = next;
    } catch (err) {
      let msg = err.message || 'Google ile giriş yapılamadı.';
      if (err.code === 'not_found' || err.code === 'phone_required') {
        msg += ' Kayıt sayfasında Google ile devam ederek üye olabilirsiniz.';
      }
      setGoogleError(msg);
    } finally {
      setGoogleLoading(false);
    }
  }

  useEffect(() => {
    const input = document.getElementById('phone_number');
    if (!input) return undefined;
    const onInput = () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 10);
    };
    input.addEventListener('input', onInput);
    return () => input.removeEventListener('input', onInput);
  }, [tab]);

  return (
    <div class="pp-auth-panel pp-auth-panel--enter">
      <header class="pp-auth-panel__head">
        <h1 class="pp-auth-panel__title">Hesabınıza Giriş Yapın</h1>
        <p class="pp-auth-panel__lead">ProParcel&apos;e katılın, profesyonel analiz araçlarını kullanın.</p>
      </header>

      {config.error ? (
        <div class="pp-auth-alert pp-auth-alert--error" role="alert">
          {config.error}
        </div>
      ) : null}

      <div class="pp-auth-tabs" role="tablist" aria-label="Giriş yöntemi">
        <button
          type="button"
          role="tab"
          class={`pp-auth-tabs__btn${tab === 'email' ? ' is-active' : ''}`}
          aria-selected={tab === 'email'}
          onClick={() => setTab('email')}
        >
          <i class="fas fa-envelope" aria-hidden="true" /> E-posta
        </button>
        <button
          type="button"
          role="tab"
          class={`pp-auth-tabs__btn${tab === 'phone' ? ' is-active' : ''}`}
          aria-selected={tab === 'phone'}
          onClick={() => setTab('phone')}
        >
          <i class="fas fa-mobile-alt" aria-hidden="true" /> Telefon
        </button>
      </div>

      {tab === 'email' ? (
        <form class="pp-auth-form" method="post" action={loginAction}>
          <input type="hidden" name="csrfmiddlewaretoken" value={config.csrfToken} />
          <input type="hidden" name="login_type" value="email" />
          <input type="hidden" name="next" value={config.nextUrl || ''} />

          <div class="pp-auth-field">
            <span class="pp-auth-field__icon" aria-hidden="true">
              <i class="fas fa-envelope" />
            </span>
            <input
              type="email"
              id="email"
              name="email"
              class={`pp-auth-input${config.formErrors?.email ? ' pp-auth-input--error' : ''}`}
              placeholder="E-posta adresiniz"
              value={config.email || ''}
              required
              autoComplete="email"
            />
          </div>
          {config.formErrors?.email ? (
            <p class="pp-auth-field-error">{config.formErrors.email}</p>
          ) : null}

          <PasswordField
            id="password"
            name="password"
            placeholder="Şifre"
            error={config.formErrors?.password}
            autoComplete="current-password"
          />
          {config.formErrors?.password ? (
            <p class="pp-auth-field-error">{config.formErrors.password}</p>
          ) : null}

          <button type="submit" class="pp-auth-btn pp-auth-btn--primary">
            <i class="fas fa-sign-in-alt" aria-hidden="true" /> Giriş Yap
          </button>
        </form>
      ) : (
        <form class="pp-auth-form" method="post" action={loginAction}>
          <input type="hidden" name="csrfmiddlewaretoken" value={config.csrfToken} />
          <input type="hidden" name="login_type" value="phone" />
          <input type="hidden" name="next" value={config.nextUrl || ''} />

          <div class="pp-auth-field pp-auth-field--phone">
            <span class="pp-auth-field__prefix">+90</span>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              class={`pp-auth-input${config.formErrors?.phone_number ? ' pp-auth-input--error' : ''}`}
              placeholder="5XX XXX XX XX"
              value={config.phoneNumber || ''}
              maxLength={10}
              pattern="[0-9]{10}"
              required
              autoComplete="tel"
            />
          </div>
          {config.formErrors?.phone_number ? (
            <p class="pp-auth-field-error">{config.formErrors.phone_number}</p>
          ) : null}

          <PasswordField
            id="password_phone"
            name="password"
            placeholder="Şifre"
            error={config.formErrors?.password}
            autoComplete="current-password"
          />

          <button type="submit" class="pp-auth-btn pp-auth-btn--primary">
            <i class="fas fa-sign-in-alt" aria-hidden="true" /> Giriş Yap
          </button>
        </form>
      )}

      <p class="pp-auth-panel__link-row">
        <a href={config.forgotPasswordUrl}>Şifremi unuttum</a>
      </p>

      <div class="pp-auth-divider">
        <span>veya</span>
      </div>

      {googleError ? (
        <div class="pp-auth-alert pp-auth-alert--error" role="alert">
          {googleError}
          {googleError.includes('Kayıt') ? null : (
            <>
              {' '}
              <a href={config.registerUrl}>Kayıt olun</a>
            </>
          )}
        </div>
      ) : null}

      {googleEnabled ? (
        <button
          type="button"
          class="pp-auth-btn pp-auth-btn--google"
          disabled={googleLoading}
          onClick={handleGoogleLogin}
        >
          <span class="pp-auth-btn__google-icon" aria-hidden="true" />
          {googleLoading ? 'Google bağlanıyor…' : 'Google ile devam et'}
        </button>
      ) : (
        <button type="button" class="pp-auth-btn pp-auth-btn--google" disabled title="Yapılandırılmamış">
          <span class="pp-auth-btn__google-icon" aria-hidden="true" />
          Google ile devam et
        </button>
      )}

      <p class="pp-auth-panel__footer">
        Hesabınız yok mu?{' '}
        <a href={config.registerUrl}>Yeni Hesap Oluştur</a>
      </p>
    </div>
  );
}
